import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createUseStyles} from 'react-jss';
import {connect} from 'react-redux';
import {updateDeliveryContactNumber} from '../../../actions/address-actions';
import {updateCollectionCollectorName} from '../../../actions/NewCollectionActions/updateCollectionCollectorName';
import FieldContainer2 from '../../../form/FieldContainer2';
import Translation from '../../../lang/Translation';
import {isResidentIDRequiredInAddressForm} from '../../../selectors/DeliveryAddressSelectors/DeliveryAddressSelectors';
import getIsUsingExpressCheckout from '../../../selectors/ExpressSelectors/getIsUsingExpressCheckout';
import getIsCollectionEnabled from '../../../selectors/getIsCollectionEnabled';
import makeGetShouldShowContactNumberAfterAddressee
  from '../../../selectors/makeGetShouldShowContactNumberAfterAddressee';
import {getPudo} from '../../../selectors/PudoSelectors';
import {
  getDeliveryType,
  getSelectedContactNumber,
  getSettings,
  getStart,
  isPudoAvailable,
} from '../../../selectors/selectors';
import {shouldShowContactNumberAfterAddressee} from '../../../selectors/shouldShowContactNumberAfterAddressee';
import store from '../../../store/store';
import Spacing from '../../../style-helper/Spacing';
import V2Components from '../../../v2-components';
import CollectionAddressee from '../../address/CollectionAddressee/CollectionAddressee';
import ContactNumber from '../../address/ContactNumber/ContactNumber';
import DeliveryAddressList from '../../address/DeliveryAddressList';
import Collection from '../../Collection/Collection';
import ResidentIDLazyLoader from '../../ImportTax/ResidentIDForm/ResidentIDLazyLoader';
import Pudo from '../../pudo/Pudo';
import Message from '../../ui/Message/Message';

const useStyles = createUseStyles({
  collectionInfoMessage: {
    marginTop: [Spacing.quad],
    marginBottom: [Spacing.quad],
  },
});

export function AddressSection(props) {
  const classes = useStyles(props);
  const fieldContainerRef = useRef(null);
  const [shouldValidateAfterNextRender, setShouldValidateAfterNextRender] = useState(false);
  const [showContactNumberAfterAddressee, setShowContactNumberAfterAddressee] = useState(false);
  
  const processErrorFieldName = (errors) => {
    const errorsWithPrefix = [];
    errors.forEach((error) => {
      errorsWithPrefix.push({
        fieldName: `delivery.address.${error.fieldName}`,
        errorCode: error.errorCode,
      });
    });
    return errorsWithPrefix;
  };
  
  const validate = useCallback(
    () => {
      if (!fieldContainerRef.current) return {isValid: true};
      
      const containerValidationResult = fieldContainerRef.current.validate();
      return {
        isValid: containerValidationResult.isValid,
        errors: (containerValidationResult.isValid)
          ? undefined
          : processErrorFieldName(containerValidationResult.errors),
      };
    }, [fieldContainerRef],
  );
  
  const validateAfterNextRender = () => {
    setShouldValidateAfterNextRender(true);
  };

  useEffect(() => {
    V2Components.register('AddressSection', {validate, validateAfterNextRender});
    return () => V2Components.clear('AddressSection');
  }, [validate]);
  
  useEffect(() => {
    if (shouldValidateAfterNextRender) {
      validate();
      setShouldValidateAfterNextRender(false);
    }
  }, [shouldValidateAfterNextRender, validate]);
  
  useEffect(() => {
    const shouldShow = shouldShowContactNumberAfterAddressee(
      store.getState(),
      props.shouldShowContactNumberAfterAddressee
    );
    setShowContactNumberAfterAddressee(shouldShow);
  }, [props.selectedAddressContactNumber]);
  
  return (
    <FieldContainer2>
      {(fieldContainer) => {
        fieldContainerRef.current = fieldContainer;
        return (
          <div className="delivery-section-address-list">
            {props.isNewCollectionOverlay && props.isDeliveryTypePudo && (
              <CollectionAddressee
                fieldContainer={fieldContainer.fieldUpdateDispatcher(props.updateCollectionCollectorName)}
                fieldName="collectionAddressee"
              />
            )}
            {(props.deliveryType === 'pudo' && props.newCollectionOverlay) && !props.usingExpressCheckout && (
              <ContactNumber
                fieldContainer={fieldContainer.fieldUpdateDispatcher(props.updateContactNumberDispatcher)}
                helpText
              />
            )}
            {props.isNewCollectionOverlay && props.isDeliveryTypePudo && (
              <div className={classes.collectionInfoMessage} data-test="collectionInfoMessage">
                <Message>
                  <Translation>checkout.delivery.address.collect.info.message</Translation>
                </Message>
              </div>
            )}
            {props.isDeliveryTypePudo
              ? (
                <div
                  className="delivery-section-address delivery-section-pudo"
                  role="tabpanel"
                  aria-labelledby="SelectableContainer--pudo"
                >
                  {props.isCollectionEnabled ? <Collection disabled={false} /> : <Pudo disabled={false} />}
                </div>
              ) : (
                <div
                  className="delivery-section-address delivery-section-residential"
                  aria-labelledby={props.isPudoAvailable ? 'SelectableContainer--residential' : undefined}
                >
                  <DeliveryAddressList
                    fieldContainer={fieldContainer}
                    disabled={false}
                  />
                </div>
              )}
            {!(props.deliveryType === 'pudo' && props.newCollectionOverlay) && !props.usingExpressCheckout && showContactNumberAfterAddressee && (
              <ContactNumber
                fieldContainer={fieldContainer.fieldUpdateDispatcher(props.updateContactNumberDispatcher)}
                helpText
                shouldValidate={props.shouldShowContactNumberAfterAddressee}
              />
            )}
            {!props.usingExpressCheckout && props.shouldShowResidentIDForm && <ResidentIDLazyLoader />}
          </div>
        );
      }}
    </FieldContainer2>
  );
}

AddressSection.propTypes = {
  shouldShowResidentIDForm: PropTypes.bool.isRequired,
  isPudoAvailable: PropTypes.bool.isRequired,
  isDeliveryTypePudo: PropTypes.bool.isRequired,
  usingExpressCheckout: PropTypes.bool.isRequired,
  isCollectionEnabled: PropTypes.bool,
  shouldShowContactNumberAfterAddressee: PropTypes.bool,
  isNewCollectionOverlay: PropTypes.bool,
  pudoCollectionPoints: PropTypes.array,
  collectorName: PropTypes.string,
  addressName: PropTypes.string,
  selectedAddressContactNumber: PropTypes.string,
  accountName: PropTypes.string,
  newCollectionOverlay: PropTypes.bool,
  deliveryType: PropTypes.string,
  
  // Actions
  updateContactNumberDispatcher: PropTypes.func.isRequired,
  updateCollectionCollectorName: PropTypes.func,
};

const mapStateToProps = (state) => {
  const deliveryType = getDeliveryType(state);
  const getShouldShowContactNumberAfterAddressee = makeGetShouldShowContactNumberAfterAddressee(deliveryType === 'pudo' ? deliveryType : 'delivery');
  const newCollectionOverlay = getSettings(state)?.should_use_new_collection_overlay;
  const accountName = getStart(state)?.customer?.fullName;
  const addressName = getPudo(state)?.savedAddresses[0]?.name;
  const collectorName = getPudo(state)?.collectorName || getPudo(state)?.savedAddresses[0]?.name || '';
  const selectedAddressContactNumber = getSelectedContactNumber(state);
  
  return {
    deliveryType,
    newCollectionOverlay,
    shouldShowResidentIDForm: isResidentIDRequiredInAddressForm(state),
    isPudoAvailable: isPudoAvailable(state),
    isDeliveryTypePudo: deliveryType === 'pudo',
    usingExpressCheckout: getIsUsingExpressCheckout(state),
    isCollectionEnabled: getIsCollectionEnabled(state),
    shouldShowContactNumberAfterAddressee: getShouldShowContactNumberAfterAddressee(state),
    isNewCollectionOverlay: newCollectionOverlay,
    collectorName,
    addressName,
    accountName,
    selectedAddressContactNumber,
  };
};

const mapDispatchToProps = dispatch => ({
  updateContactNumberDispatcher: (_, value) => {
    dispatch(updateDeliveryContactNumber(value));
  },
  // TODO: the below variable is unused
  updateCollectionCollectorName: (name, value) => {
    dispatch(updateCollectionCollectorName(name, value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AddressSection);
